import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  onSnapshot,
  type Unsubscribe,
} from '@/lib/firestore'
import { db } from '@/config/firebase'
import { useAuthStore } from '@/stores/auth'
import { getTenantConstraint, getTenantData } from '@/utils/tenantQuery'
import { useActivityLog } from '@/composables/useActivityLog'
import { useNotificationsStore } from '@/stores/notifications'
import type {
  Implementation,
  ImplementationStatus,
  ImplementationType,
} from '@/types'
import type { ImplementationsState } from './state'

export interface CreateImplementationData {
  projectId: string
  organizationId: string
  applicationId?: string
  name: string
  description?: string
  type: ImplementationType
  status: ImplementationStatus
  operatorIds?: string[]
  environments?: any[]
}

export interface UpdateImplementationData {
  name?: string
  description?: string
  applicationId?: string
  type?: ImplementationType
  status?: ImplementationStatus
  operatorIds?: string[]
  environments?: any[]
}

// Remove undefined values from an object (Firestore rejects undefined)
const stripUndefined = (obj: Record<string, unknown>): Record<string, unknown> => {
  return Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== undefined)
  )
}

export const createActions = (state: ImplementationsState) => {
  const { logActivity } = useActivityLog()
  const notificationsStore = useNotificationsStore()

  let globalUnsubscribe: Unsubscribe | null = null

  const mapImplementationDoc = (d: { id: string; data: () => Record<string, unknown> }): Implementation => {
    const data = d.data()
    return {
      id: d.id,
      tenantId: data.tenantId,
      ...data,
      operatorIds: data.operatorIds || [],
      environments: data.environments || [],
      createdAt: (data.createdAt as any)?.toDate?.() || new Date(),
      updatedAt: (data.updatedAt as any)?.toDate?.() || new Date(),
    } as Implementation
  }

  const subscribeGlobal = (): void => {
    if (globalUnsubscribe) return
    state.isLoading = true
    const authStore = useAuthStore()
    const tenantConstraint = getTenantConstraint()
    const baseConstraints = tenantConstraint ? [tenantConstraint] : []

    let q = query(collection(db, 'implementations'), ...baseConstraints, orderBy('createdAt', 'desc'))

    if (authStore.user?.role === 'client' || authStore.user?.role === 'member') {
      if (authStore.user.organizationId) {
        q = query(collection(db, 'implementations'), ...baseConstraints, where('organizationId', '==', authStore.user.organizationId), orderBy('createdAt', 'desc'))
      }
    } else if (authStore.user?.role === 'operator') {
      q = query(collection(db, 'implementations'), ...baseConstraints, where('operatorIds', 'array-contains', authStore.user.id), orderBy('createdAt', 'desc'))
    }

    globalUnsubscribe = onSnapshot(q, (snapshot) => {
      state.implementations = snapshot.docs.map(mapImplementationDoc)
      state.isLoading = false
    }, (error) => {
      console.error('Implementations subscription error:', error)
      state.isLoading = false
    })
  }

  const unsubscribeGlobal = (): void => {
    if (globalUnsubscribe) {
      globalUnsubscribe()
      globalUnsubscribe = null
    }
  }

  const fetchImplementations = async (projectId?: string): Promise<void> => {
    state.isLoading = true
    state.error = null

    try {
      const authStore = useAuthStore()
      const tenantConstraint = getTenantConstraint()
      const baseConstraints = tenantConstraint ? [tenantConstraint] : []
      let q = query(collection(db, 'implementations'), ...baseConstraints, orderBy('createdAt', 'desc'))

      if (projectId) {
        q = query(
          collection(db, 'implementations'),
          ...baseConstraints,
          where('projectId', '==', projectId),
          orderBy('createdAt', 'desc')
        )
      } else if (authStore.user?.role === 'client' || authStore.user?.role === 'member') {
        if (authStore.user.organizationId) {
          q = query(
            collection(db, 'implementations'),
            ...baseConstraints,
            where('organizationId', '==', authStore.user.organizationId),
            orderBy('createdAt', 'desc')
          )
        }
      } else if (authStore.user?.role === 'operator') {
        q = query(
          collection(db, 'implementations'),
          ...baseConstraints,
          where('operatorIds', 'array-contains', authStore.user.id),
          orderBy('createdAt', 'desc')
        )
      }

      const snapshot = await getDocs(q)
      state.implementations = snapshot.docs.map(mapImplementationDoc)
    } catch (error) {
      console.error('Error fetching implementations:', error)
      state.error = 'Error al cargar las implementaciones'
    } finally {
      state.isLoading = false
    }
  }

  const getImplementationById = async (id: string): Promise<Implementation | null> => {
    try {
      const docRef = doc(db, 'implementations', id)
      const docSnap = await getDoc(docRef)

      if (docSnap.exists()) {
        const implementation = mapImplementationDoc(docSnap)
        state.selectedImplementation = implementation
        return implementation
      }

      return null
    } catch (error) {
      console.error('Error fetching implementation:', error)
      state.error = 'Error al cargar la implementacion'
      return null
    }
  }

  const createImplementation = async (data: CreateImplementationData): Promise<string> => {
    state.isLoading = true
    state.error = null

    try {
      const authStore = useAuthStore()
      const userId = authStore.user?.id || ''

      const implementationData = stripUndefined({
        ...getTenantData(),
        ...data,
        operatorIds: data.operatorIds || [],
        environments: data.environments || [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: userId,
        updatedBy: userId,
      })

      const docRef = await addDoc(collection(db, 'implementations'), implementationData)

      await logActivity(
        'implementation',
        docRef.id,
        'created',
        `Se creo la implementacion "${data.name}"`
      )

      await notificationsStore.notifyAdminsAndOperators(
        'project_created', // Replace with a more specific notification if available
        'Nueva implementacion creada',
        `Se ha creado la implementacion "${data.name}"`,
        'implementation',
        docRef.id
      )

      return docRef.id
    } catch (error) {
      console.error('Error creating implementation:', error)
      state.error = 'Error al crear la implementacion'
      throw error
    } finally {
      state.isLoading = false
    }
  }

  const updateImplementation = async (id: string, data: UpdateImplementationData): Promise<void> => {
    state.isLoading = true
    state.error = null

    try {
      const authStore = useAuthStore()
      const userId = authStore.user?.id || ''

      const updateData = stripUndefined({
        ...data,
        updatedAt: serverTimestamp(),
        updatedBy: userId,
      })

      await updateDoc(doc(db, 'implementations', id), updateData)

      await logActivity(
        'implementation',
        id,
        data.status ? 'status_changed' : 'updated',
        data.status
          ? `Se cambio el estado de la implementacion a "${data.status}"`
          : 'Se actualizo la implementacion'
      )

      // Update local state if not subscribed globally (or to avoid waiting for snapshot)
      const index = state.implementations.findIndex((a) => a.id === id)
      if (index !== -1) {
        state.implementations[index] = {
          ...state.implementations[index],
          ...data,
          updatedAt: new Date(),
        }
      }

      if (state.selectedImplementation?.id === id) {
        state.selectedImplementation = {
          ...state.selectedImplementation,
          ...data,
          updatedAt: new Date(),
        }
      }
    } catch (error) {
      console.error('Error updating implementation:', error)
      state.error = 'Error al actualizar la implementacion'
      throw error
    } finally {
      state.isLoading = false
    }
  }

  const deleteImplementation = async (id: string): Promise<void> => {
    state.isLoading = true
    state.error = null

    try {
      const implementation = state.implementations.find((a) => a.id === id)

      await deleteDoc(doc(db, 'implementations', id))

      await logActivity(
        'implementation',
        id,
        'deleted',
        `Se elimino la implementacion "${implementation?.name || id}"`
      )

      state.implementations = state.implementations.filter((a) => a.id !== id)

      if (state.selectedImplementation?.id === id) {
        state.selectedImplementation = null
      }
    } catch (error) {
      console.error('Error deleting implementation:', error)
      state.error = 'Error al eliminar la implementacion'
      throw error
    } finally {
      state.isLoading = false
    }
  }

  const setFilters = (filters: Partial<ImplementationsState['filters']>): void => {
    state.filters = { ...state.filters, ...filters }
  }

  const clearFilters = (): void => {
    state.filters = {
      search: '',
      projectId: '',
      applicationId: '',
      status: '',
      type: '',
    }
  }

  const setSelectedImplementation = (implementation: Implementation | null): void => {
    state.selectedImplementation = implementation
  }

  return {
    subscribeGlobal,
    unsubscribeGlobal,
    fetchImplementations,
    getImplementationById,
    createImplementation,
    updateImplementation,
    deleteImplementation,
    setFilters,
    clearFilters,
    setSelectedImplementation,
  }
}
